/* eslint-disable no-underscore-dangle */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
// adopt
import { adopt } from 'react-adopt';
// graphQL
import gql from 'graphql-tag';
// global variables
import {
  defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS,
  reviewReportStatus,
} from 'config';
// our components
import { Mutation, Query } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import {
  DISMISS_REVIEW_REPORTS_MUTATION,
  RESOLVE_REVIEW_REPORTS_MUTATION,
} from 'components/ReviewReport/mutations';
import qs, { getQueryString } from 'helpers/queryStrings';
import RouterContext from 'helpers/routerContext';
import ReviewReportList from './List';

/**
 * Query to fetch review reports from api.
 */
export const REVIEW_REPORTS_QUERY = gql`
  query ReviewReportsList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $id: ID
    $status: [FilterByStatus]
    $reason: [FilterByReason]
    $review: ID
    $reporter: ID
    $reviewer: ID
    $reviewee: ID
    $reviewedBy: ID
    $createdOnGte: DateTime
    $createdOnLte: DateTime
    $reviewedOnGte: DateTime
    $reviewedOnLte: DateTime
  ) {
    reviewReports(
      first: $first
      after: $after
      last: $last
      before: $before
      id: $id
      status: $status
      reason: $reason
      review: $review
      reporter: $reporter
      reviewer: $reviewer
      reviewee: $reviewee
      reviewedBy: $reviewedBy
      createdOnGte: $createdOnGte
      createdOnLte: $createdOnLte
      reviewedOnGte: $reviewedOnGte
      reviewedOnLte: $reviewedOnLte
    ) @connection(key: "review_reports") {
      meta {
        total
      }
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          id
          reason
          comment
          status
          createdOn
          updatedOn
          reviewedOn
          reporter {
            id
            fullname
          }
          reviewer {
            id
            fullname
          }
          reviewee {
            id
            fullname
          }
          reviewedBy {
            id
            fullname
          }
          review {
            id
            rate
            comment
            status
            reviewedBy {
              id
              fullname
            }
            reviewer {
              id
              fullname
            }
            reviewee {
              id
              fullname
            }
          }
        }
      }
    }
  }
  ${PageInfo}
`;

const ALL_STATUS_FILTER = 'all';

const isEmptyArrayFilterValue = value =>
  value === undefined ||
  value === null ||
  value === '' ||
  value === 'null' ||
  value === ALL_STATUS_FILTER;

const normalizeArrayFilter = value => {
  if (isEmptyArrayFilterValue(value)) {
    return undefined;
  }
  if (Array.isArray(value)) {
    const values = value.filter(item => !isEmptyArrayFilterValue(item));
    return values.length > 0 ? values : undefined;
  }
  return [value];
};

const normalizeNullableDate = value => {
  if (value === null || value === 'null') {
    return undefined;
  }
  return value;
};

export const defaultFilters = {
  id: undefined,
  status: [reviewReportStatus.OPEN],
  reason: undefined,
  review: undefined,
  reporter: undefined,
  reviewer: undefined,
  reviewee: undefined,
  reviewedBy: undefined,
  createdOnGte: undefined,
  createdOnLte: undefined,
  reviewedOnGte: undefined,
  reviewedOnLte: undefined,
};

const getDefaultFilters = () => ({
  ...defaultFilters,
  status: [...defaultFilters.status],
});

class ReviewReportListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;
    const {
      direction,
      cursor,
      numberOfRowsSelected,
      ...initFilters
    } = initialFilters;
    this.state = {
      filters: this.normalizeFilters({
        ...getDefaultFilters(),
        ...initFilters,
      }),
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 0) || DEFAULT_NUMBER_OF_ROWS,
      itemProcessing: [],
      failedIds: [],
      errors: [],
      showNotification: false,
      pagination: { direction: direction || null, cursor: cursor || undefined },
    };
    this.Compose = adopt({
      mutateResolveReviewReports: this.resolveReviewReports,
      mutateDismissReviewReports: this.dismissReviewReports,
      query: this.listReviewReports,
    });
    this.fetchMore = () => {};
  }

  normalizeFilters = filters => ({
    ...filters,
    status: normalizeArrayFilter(filters.status),
    reason: normalizeArrayFilter(filters.reason),
    createdOnGte: normalizeNullableDate(filters.createdOnGte),
    createdOnLte: normalizeNullableDate(filters.createdOnLte),
    reviewedOnGte: normalizeNullableDate(filters.reviewedOnGte),
    reviewedOnLte: normalizeNullableDate(filters.reviewedOnLte),
  });

  /**
   * @description
   * Query callback to compose with mutations.
   *
   * @param {*} render - Query's render props.
   * @returns {*} Query component.
   */
  listReviewReports = ({ render }) => {
    const { numberOfRowsSelected, filters, pagination } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };

    return (
      <Query
        query={REVIEW_REPORTS_QUERY}
        variables={{
          ...paginationVariables,
          ...filters,
        }}
        notifyOnNetworkStatusChange
        fetchPolicy="cache-and-network"
      >
        {({ data, loading, fetchMore }) => {
          this.fetchMore = fetchMore;
          return render({ data, loading, fetchMore });
        }}
      </Query>
    );
  };

  /**
   * @description
   * Mutation resolve callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  resolveReviewReports = ({ render }) => (
    <Mutation
      mutation={RESOLVE_REVIEW_REPORTS_MUTATION}
      onCompleted={this.mutationCompleted('resolveReviewReports')}
    >
      {(handleResolveReviewReports, { loading }) =>
        render({ handleResolveReviewReports, loadingResolve: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * Mutation dismiss callback.
   *
   * @param {*} render - Mutation's render props.
   * @returns {*} Mutation component.
   */
  dismissReviewReports = ({ render }) => (
    <Mutation
      mutation={DISMISS_REVIEW_REPORTS_MUTATION}
      onCompleted={this.mutationCompleted('dismissReviewReports')}
    >
      {(handleDismissReviewReports, { loading }) =>
        render({ handleDismissReviewReports, loadingDismiss: loading })
      }
    </Mutation>
  );

  /**
   * @description
   * Filter rows according selected filters.
   *
   * @param {Object} filters - Filters to apply.
   */
  handleFilter = filters => {
    const history = this.context;
    const nextFilters = this.normalizeFilters(filters);
    this.setState({
      filters: nextFilters,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...nextFilters,
      status:
        filters.status === undefined ? ALL_STATUS_FILTER : nextFilters.status,
      createdOnGte:
        filters.createdOnGte === null ? 'null' : nextFilters.createdOnGte,
      createdOnLte:
        filters.createdOnLte === null ? 'null' : nextFilters.createdOnLte,
      reviewedOnGte:
        filters.reviewedOnGte === null ? 'null' : nextFilters.reviewedOnGte,
      reviewedOnLte:
        filters.reviewedOnLte === null ? 'null' : nextFilters.reviewedOnLte,
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Change state to restart filters.
   */
  handleRestartFilters = () => {
    const history = this.context;
    const filters = this.normalizeFilters(getDefaultFilters());
    this.setState({
      filters,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...filters,
      numberOfRowsSelected,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * OnComplete mutations.
   *
   * @param {Object} field - Target field of mutation.
   * @returns {undefined} - No returns value.
   */
  mutationCompleted = field => async data => {
    const { itemProcessing } = this.state;
    const payload = data[field] || {};
    const successIds = payload.successIds || [];
    const failedIds = itemProcessing.filter(item => {
      const stringId = `${item}`;
      return (
        successIds.indexOf(item) === -1 && successIds.indexOf(stringId) === -1
      );
    });
    const hasErrors = Boolean(payload.errors && payload.errors.length > 0);
    this.setState({
      itemProcessing: [],
      failedIds,
      errors: payload.errors || [],
      showNotification: failedIds.length > 0 || hasErrors,
    });
    this.handleRefresh();
  };

  /**
   * @description
   * Change number of rows selected.
   *
   * @param {number} selectedItem - New number of rows selected.
   */
  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Fetch more rows when user change page.
   *
   * @param {Object} paginatorVariables - Pagination query's variables.
   */
  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  /**
   * @description
   * Refresh the list.
   * @returns {Function} - Fetch More function.
   */
  handleRefresh = () => {
    this.fetchMore({
      updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
    });
  };

  /**
   * @description
   * Close notification.
   */
  handleCloseNotification = () => {
    this.setState({ showNotification: false, errors: [] });
  };

  render() {
    const {
      numberOfRowsSelected,
      filters,
      itemProcessing,
      pagination,
      failedIds,
      errors,
      showNotification,
    } = this.state;
    return (
      <this.Compose>
        {({
          mutateResolveReviewReports: {
            handleResolveReviewReports,
            loadingResolve,
          },
          mutateDismissReviewReports: {
            handleDismissReviewReports,
            loadingDismiss,
          },
          query: { data, loading },
        }) => (
          <ReviewReportList
            data={data}
            appliedFilter={filters}
            paginationDirection={pagination.direction}
            filter={this.handleFilter}
            restartFilters={this.handleRestartFilters}
            resolveReports={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
                errors: [],
              });
              handleResolveReviewReports({ variables: { ids: items } });
            }}
            dismissReports={items => {
              this.setState({
                itemProcessing: [...itemProcessing, ...items],
                failedIds: [],
                errors: [],
              });
              handleDismissReviewReports({ variables: { ids: items } });
            }}
            numberOfRowsSelected={numberOfRowsSelected}
            changePage={paginatorVariables => {
              this.handleChangePage(paginatorVariables);
            }}
            refresh={this.handleRefresh}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            loading={Object.keys(data).length === 0}
            fullLoading={loading || loadingResolve || loadingDismiss}
            itemProcessing={itemProcessing}
            failedIds={failedIds}
            errors={errors}
            showNotification={showNotification}
            closeNotification={this.handleCloseNotification}
          />
        )}
      </this.Compose>
    );
  }
}

ReviewReportListContainer.propTypes = {
  initialFilters: PropTypes.shape({}).isRequired,
};

export default ReviewReportListContainer;
